import { ApiOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { ResourceRule } from "@p0security/shared/types/policy/types";
import { Flex, Typography } from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import {
  RoutingRulePopover,
  StyledRoutingRulePreview,
} from "components/Jit/Policy/styles";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { useMemo } from "react";

import { useInstalledIntegrations } from "../../providers/InstalledIntegrationsProvider";
import { ResourceAccessTypeLabel } from "./ResourceAccessTypeLabel";

export type ResourceRulePreview = ResourceRule | { type: "genericIntegration" };

type Props = {
  resource: ResourceRulePreview;
};

export const ResourcePreview: React.FC<Props> = ({ resource }) => {
  const { integrationMap, installedResources } = useInstalledIntegrations();

  const resourceFilters = useMemo(() => {
    if (resource.type !== "integration") {
      return [];
    }

    // Must be after the resource.type check above
    const { filters } = resource;

    if (!filters) {
      return [];
    }

    return Object.keys(filters).map((key) => ({
      filterType: key,
      filter: filters[key as keyof typeof filters],
    }));
  }, [resource]);

  return resource.type === "genericIntegration" ? (
    <StyledRoutingRulePreview>
      <ApiOutlined />
      <span>Resource integration</span>
    </StyledRoutingRulePreview>
  ) : resource.type === "any" ? (
    <RoutingRulePopover
      text="Any installed resource"
      icon={<ApiOutlined />}
      popoverContent={
        <Flex vertical gap={8}>
          {installedResources.length > 0 && (
            <Flex vertical gap={4}>
              {installedResources.map((i) => {
                return (
                  <Flex align="center" key={i.label} gap={8}>
                    {isKeyOf(AllIntegrationLogos, i.key) &&
                      AllIntegrationLogos[i.key]}
                    <Typography.Text>{i.label}</Typography.Text>
                  </Flex>
                );
              })}
            </Flex>
          )}
          <TenantAwareLink to={AppPaths.Integrations}>
            Manage integrations
          </TenantAwareLink>
        </Flex>
      }
    />
  ) : (
    <ResourceAccessTypeLabel
      integrationMap={integrationMap}
      resource={resource}
      resourceFilters={resourceFilters}
    />
  );
};
